import Dialog from "@material-ui/core/Dialog";
import { withStyles, WithStyles } from "@material-ui/core/styles";
import classNames from "classnames";
import React, { ReactNode } from "react";
import { useFrame } from "react-frame-component";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { useRouteMatch } from "react-router-dom";
import Button from "Shared/Button";
import { useMediaQueries } from "Shared/Responsive";
import CloseIcon from "src/components/icons/Close";
import { closeCreditIQPrequal } from "src/store/actions/creditIQ";
import { closeTradeIn } from "src/store/actions/tradeIn";
import useSelector from "src/utils/useSelector";
import { SubModalType } from "Types/state";
import "./Modal.scss";
import modalTypes from "./modalTypes";

const nonDrawerSubRoutes = ["/my-garage/test-drive"];

const styles = {
    paper: {
        backgroundColor: "transparent",
        borderRadius: 0,
        boxShadow: "none",
        marginTop: "70px !important",
    },
    paperFullScreen: {
        backgroundColor: "#FFFFFF",
        marginTop: "0px !important",
    },
    root: {
        paddingRight: "0 !important",
    },
};

export interface ModalComponentProps {
    closeModal: () => void;
}

export interface ModalProps {
    className?: string;
    closeModal: () => void;
    fullScreen?: boolean;
    id?: string;
    maxWidth?: string;
    modalType?: SubModalType;
    open: boolean;
}

function Modal(props: ModalProps & WithStyles & { children?: ReactNode }) {
    const iframe = useFrame();
    const dispatch = useDispatch();
    const shouldCloseTradeIn = useSelector((state) => state.onlineShopper.tradeInOpen);
    const shouldClosePrequal = useSelector((state) => state.onlineShopper.creditIQPrequalOpen);

    const shouldNavigateModal = shouldClosePrequal || shouldCloseTradeIn;
    const closeTrade = () => dispatch(closeTradeIn());
    const closePrequal = () => dispatch(closeCreditIQPrequal());

    const { notDesktop } = useMediaQueries();

    const { classes, className, fullScreen, id, maxWidth = "720px", open, closeModal, modalType } = props;

    const ModalComponent = modalType && modalTypes[modalType] ? modalTypes[modalType] : null;

    const modalContent = ModalComponent ? <ModalComponent closeModal={closeModal} /> : null;

    const isFullScreen = fullScreen || notDesktop;

    const garageRouteMatch = useRouteMatch("/my-garage/:subRoute");
    const paymentBuilderRouteMatch = useRouteMatch("/payment-builder");
    const history = useHistory();

    const isHiddenCloseButton =
        isFullScreen &&
        modalType === undefined &&
        garageRouteMatch &&
        !nonDrawerSubRoutes.some((subRoute) => subRoute === garageRouteMatch?.url);

    const isFixedCloseButton = isFullScreen && paymentBuilderRouteMatch;

    return (
        <Dialog
            hideBackdrop={ModalComponent === null && !!document.getElementById("os-ddc-temp-backdrop")}
            BackdropProps={{ style: { backgroundColor: "rgba(25, 26, 26, 0.7)" } }}
            className={classNames(className)}
            classes={{
                paper: classes.paper,
                paperFullScreen: classes.paperFullScreen,
                root: classes.root,
            }}
            style={modalContent ? { zIndex: 1220302 } : undefined}
            disableBackdropClick={true}
            fullScreen={isFullScreen}
            open={open}
            PaperProps={{
                square: true,
                style: {
                    maxWidth: isFullScreen ? "none" : `calc(${maxWidth} + 60px)`,
                    overflow: isFullScreen ? "auto" : "hidden",
                    zIndex: 5,
                },
            }}
            container={iframe.document.getElementById("root")}
            scroll="body"
        >
            <div id={id} className="Modal__content" data-e2e="modal">
                {modalContent || props.children}
            </div>
            {!isHiddenCloseButton && (
                <Button
                    className={classNames("Modal__close-button", {
                        "Modal__close-button-fixed": isFixedCloseButton,
                    })}
                    onClick={() => {
                        if (shouldNavigateModal) {
                            shouldClosePrequal && closePrequal();
                            shouldCloseTradeIn && closeTrade();
                        } else {
                            closeModal();
                        }

                        if (modalType === undefined) {
                            history.push("/");
                        }
                    }}
                    data-e2e="modalCloseButton"
                >
                    <CloseIcon className="Modal__close-button-icon" />
                </Button>
            )}
        </Dialog>
    );
}

export default withStyles(styles, { index: 1 })(Modal);
